import { createAsyncThunk, isFulfilled, isPending } from '@reduxjs/toolkit';
import { apiProcess } from 'app/config/axios-interceptor';
import { createEntitySlice, EntityState, serializeAxiosError } from 'app/shared/reducers/reducer.utils';
import { cleanEntity } from 'app/shared/util/entity-utils';

const initialState: EntityState<any> = {
  loading: false,
  errorMessage: null,
  entities: [],
  entity: {},
  viewing: false,
  updating: false,
  totalItems: 0,
  updateSuccess: false,
};

const apiUrl = 'api/evaluate-configures';
const apiUrlExport = 'api/export-excels';

// Actions

export const getEntities = createAsyncThunk(
  'evaluationLevel/fetch_entity_list',
  async ({ ownerId, planPeriodId }: any) => {
    return apiProcess.get<any[]>(apiUrl, {
      params: {
        ownerId,
        planPeriodId,
        cacheBuster: new Date().getTime(),
      },
    });
  },
  { serializeError: serializeAxiosError },
);

export const getEntity = createAsyncThunk(
  'evaluationLevel/fetch_entity',
  async (id: string | number) => {
    const requestUrl = `${apiUrl}/${id}`;
    return apiProcess.get<any>(requestUrl);
  },
  { serializeError: serializeAxiosError },
);

export const createEntity = createAsyncThunk(
  'evaluationLevel/create_entity',
  async (entity: any, thunkAPI) => {
    const result = await apiProcess.post<any>(apiUrl, cleanEntity(entity));
    return result;
  },
  { serializeError: serializeAxiosError },
);

export const updateEntity = createAsyncThunk(
  'evaluationLevel/update_entity',
  async (entity: any, thunkAPI) => {
    const result = await apiProcess.put<any>(`${apiUrl}`, cleanEntity(entity));
    return result;
  },
  { serializeError: serializeAxiosError },
);

export const deleteEntity = createAsyncThunk(
  'evaluationLevel/delete_entity',
  async (id: string | number, thunkAPI) => {
    const requestUrl = `${apiUrl}/${id}`;
    const result = await apiProcess.delete<any>(requestUrl);
    return result;
  },
  { serializeError: serializeAxiosError },
);

export const exportExcel = createAsyncThunk(
  'evaluationLevel/delete_entity',
  async ({ ownerId, reportScheduleId, scopeOfOperationType }: any) => {
    const requestUrl = `${apiUrlExport}/result-evaluation`;
    const result = await apiProcess.get<any>(requestUrl, {
      params: {
        ownerId,
        planPeriodId: reportScheduleId,
        scopeOfOperationType,
      },
      responseType: 'blob', // Set responseType to 'blob' for binary data
      timeout: 120000,
    });
    return result;
  },
  { serializeError: serializeAxiosError },
);

// slice

export const EvaluationLevelSlice = createEntitySlice({
  name: 'evaluationLevel',
  initialState,
  extraReducers(builder) {
    builder
      .addCase(getEntity.fulfilled, (state, action) => {
        state.viewing = false;
        state.entity = action.payload.data;
      })
      .addCase(deleteEntity.fulfilled, state => {
        state.updating = false;
        state.updateSuccess = true;
        state.entity = {};
      })
      .addMatcher(isFulfilled(getEntities), (state, action) => {
        const { data, headers } = action.payload;

        return {
          ...state,
          loading: false,
          entities: data,
          totalItems: parseInt(headers['x-total-count'], 10),
        };
      })
      .addMatcher(isFulfilled(createEntity, updateEntity), (state, action) => {
        state.updating = false;
        state.loading = false;
        state.updateSuccess = true;
        state.entity = action.payload.data;
      })
      .addMatcher(isPending(getEntities), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.loading = true;
        state.entities = [];
      })
      .addMatcher(isPending(getEntity), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.viewing = true;
        state.entity = {};
      })
      .addMatcher(isPending(createEntity, updateEntity, deleteEntity), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.updating = true;
      });
  },
});

export const { reset } = EvaluationLevelSlice.actions;

// Reducer
export default EvaluationLevelSlice.reducer;
